/**
 * 图表数值展示格式轻量默认映射。
 *
 * @see types.ts · utils.ts
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import { FieldType, type TFieldType } from '@dbl/core/dataSource';

import { NumericalUnit, type TNumericalUnit } from './types';

/**
 * 按 chart_type 决定数值字段默认 unit（BigNumber 类用 ORIGINAL，其余多为 BMK）。
 *
 * @core SortableDrag 持久化、BatchAddFieldPopover、DefineField 与字段编辑面板共用；
 *       不属于任一 TragCard 或单页面私有。
 */
export const ChartTypeDefaultUnit = {
  [ChartType.Table]: NumericalUnit.ORIGINAL,
  [ChartType.Pie]: NumericalUnit.BMK,
  [ChartType.Bar]: NumericalUnit.BMK,
  [ChartType.HorizontalBarChart]: NumericalUnit.BMK,
  [ChartType.StackBar]: NumericalUnit.BMK,
  [ChartType.Line]: NumericalUnit.BMK,
  [ChartType.EventLine]: NumericalUnit.BMK,
  [ChartType.MixedChart]: NumericalUnit.BMK,
  [ChartType.BigNumber]: NumericalUnit.ORIGINAL,
  [ChartType.Trend]: NumericalUnit.ORIGINAL,
  [ChartType.DimNumberTrend]: NumericalUnit.ORIGINAL,
  [ChartType.Map]: NumericalUnit.BMK,
  [ChartType.WordCloud]: NumericalUnit.BMK,
  [ChartType.PercentStackBar]: NumericalUnit.BMK,
  [ChartType.PercentLine]: NumericalUnit.BMK,
} as const satisfies Record<TChartType, TNumericalUnit>;

/**
 * 按 column_type 补齐默认 accuracy（小数位数）。
 *
 * @core 拖拽建图落库、字段类型切换、批量加字段等多入口写同一默认值。
 */
export const FieldTypeDefaultAccuracy = {
  [FieldType.String]: 0,
  [FieldType.Date]: 0,
  [FieldType.Time]: 0,
  [FieldType.Number]: 0,
  [FieldType.Float]: 2,
  [FieldType.Percent]: 2,
  [FieldType.BigInt]: 0,
  [FieldType.Decimal]: 2,
} as const satisfies Partial<Record<TFieldType, number>>;

/**
 * 补齐缺失 unit 时，对 Number / count 聚合字段同时写 accuracy=2 的 chart_type 集合。
 *
 * @core getOldChartDisplayConfig 与 Dashboard 各 TragCard 读显路径共用，避免各卡片重复维护列表。
 */
export const CHART_TYPES_WITH_COUNT_UNIT_DEFAULT_ACCURACY = new Set<TChartType>([
  ChartType.Pie,
  ChartType.Bar,
  ChartType.HorizontalBarChart,
  ChartType.StackBar,
  ChartType.Line,
  ChartType.EventLine,
  ChartType.MixedChart,
  ChartType.WordCloud,
]);
